import { useEffect, useRef, useState } from 'react';
import { SolidButton } from '../Button';
import CustomInput from '../CustomInput';
import { P } from 'components/Text';
import { useTranslation } from 'next-i18next/pages';
import RadioButton from 'components/RadioButton';
import { useRouter } from 'next/router';
import useUpdateOrderFirebaseUserId from 'hooks/useUpdateOrderFirebaseUserId';
import Script from 'next/script';
import useSignup from 'hooks/useSignup';
import {
  EmailAuthProvider,
  UserCredential,
  linkWithCredential,
  signInAnonymously,
} from 'firebase/auth';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from 'config/initAuth';
import { useAuthContext } from 'context/AuthContext';

interface Props {
  closeModal: () => void;
  content?: any;
}

interface ISignupFormIF {
  email: string;
  firstName: string;
  lastName: string;
  password1: string;
  password2: string;
}

const SignupForm: React.FC<Props> = (props: Props) => {
  const { closeModal, content } = props;
  const router = useRouter();
  const [registrationType, setRegistrationType] = useState<
    'individual' | 'organization'
  >('individual');
  const [formState, setFormState] = useState<ISignupFormIF>({
    email: '',
    firstName: '',
    lastName: '',
    password1: '',
    password2: '',
  });
  const [error, setError] = useState<string | null>(null);
  const handleChange = ({
    target: { name, value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  // Storing the widget id
  const turnstileWidgetId = useRef<string | null>(null);

  const renderWidget = () => {
    (window as any).turnstile?.render('#turnstile-widget-container', {
      sitekey: `${process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}`,
      callback: function (token: any) {
        const inputElement: HTMLInputElement | null = document.querySelector(
          'input[name="cf-turnstile-response"]',
        );
        if (inputElement && inputElement.id) {
          const widgetId = inputElement.id.replace('_response', '');
          turnstileWidgetId.current = widgetId;
        }
      },
    });
  };

  const { t } = useTranslation();
  const { authUser: user } = useAuthContext();
  const { fetchData } = useUpdateOrderFirebaseUserId();
  const { fetchData: signUp } = useSignup();
  useEffect(() => {
    return () => {
      // Removing turnstile widget on unmount
      const turnstile = (window as any).turnstile;
      if (turnstile && turnstileWidgetId && turnstileWidgetId.current) {
        return turnstile.remove(turnstileWidgetId.current);
      }
    };
  }, []);

  const updateBookingUserId = (creds: UserCredential) => {
    const { id } = router.query;
    if (
      (router.pathname === '/bookings' ||
        router.pathname === '/notifications') &&
      creds?.user?.uid &&
      id
    ) {
      // Updating the user id for the booking on signup at bookings or notifications page
      // Update the order's usersId
      fetchData(id as string, creds.user);
      setError(null);
      closeModal();
      // Reload the page
      router.replace(router.pathname);
      return;
    }
  };

  const onSignup = async (evt: any) => {
    if (!(formState.email && formState.password1 && formState.password2))
      return;
    if (formState.password1 !== formState.password2) {
      setError(t('Passwords do not match'));
      return;
    }
    // Selecting the div which contains the widget token
    const inputElement: HTMLInputElement | null = document.querySelector(
      'input[name="cf-turnstile-response"]',
    );

    if (user?.isAnonymous) {
      const credential = EmailAuthProvider.credential(
        formState.email,
        formState.password1,
      );

      if (credential && auth.currentUser) {
        try {
          const creds = await linkWithCredential(auth.currentUser, credential);
          updateBookingUserId(creds);
          resumeSignup();
          setError(null);
          closeModal();
        } catch (ex: any) {
          setError(ex.message);
        }
      }
    } else {
      const response = await signUp(
        formState.email,
        formState.password1,
        inputElement?.value,
      );
      if (response.status === 200) {
        if (response.message !== '') {
          try {
            const creds = await signInWithCustomToken(auth, response.message);
            updateBookingUserId(creds);
            resumeSignup();
            setError(null);
            closeModal();
          } catch (exception) {
            setError(t('Sign up error, please try again later...'));
          }
        } else {
          setError(t('Sign up error, please try again later...'));
        }
      } else {
        // If error occurred during the registration, display the error message and render a new widget
        // because the previous widget was invalidated
        setError(t(response.message));
        renderWidget();
      }
    }
  };

  useEffect(() => {
    // Manually creating the widget if the widget id doesn't exist
    if (!turnstileWidgetId.current) {
      renderWidget();
    }
  }, []);

  useEffect(() => {
    // Registered in an effect, which runs before the Turnstile script (loaded
    // by next/script after hydration) can call it.
    (window as any).onloadTurnstileCallback = function () {
      // On turnstile load, creating a widget
      (window as any).turnstile?.render('#turnstile-widget-container', {
        sitekey: `${process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}`,
        callback: () => {
          renderWidget();
        },
      });
    };
  }, []);

  const onFormSubmit = (e: any) => {
    e.preventDefault();
    onSignup(e);
  };

  const resumeSignup = () => {
    // Resumes the login if an url was given
    if (content) {
      const { next, query } = content;
      if (next) {
        router.push({ pathname: next, query: query || '' });
      }
    } else {
      closeModal();
    }
  };

  const handleContinueWithoutRegistration = () => {
    const handleAnonymousLogin = async () => {
      await signInAnonymously(auth);
      resumeSignup();
    };

    handleAnonymousLogin();
  };

  return (
    <form
      className="flex flex-col space-y-4 mt-4 w-full md:w-4/5"
      onSubmit={onFormSubmit}>
      {error && <P>{error}</P>}
      <div className="flex">
        <div className="form-check form-check-inline">
          <RadioButton
            id="individual"
            idx={1}
            handleSelect={() => setRegistrationType('individual')}
            checked={registrationType === 'individual'}
            htmlFor="individual"
            labelTitle={t('Individual')}
            value="individual"
            name="individualOrganization"
          />
        </div>
        <div className="form-check form-check-inline ml-4">
          <RadioButton
            id="organization"
            disabled
            idx={2}
            handleSelect={() => setRegistrationType('organization')}
            checked={false}
            htmlFor="organization"
            labelTitle={t('Organization')}
            value="organization"
            name="individualOrganization"
          />
        </div>
      </div>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback"
        defer={true}
      />
      {/* The Turnstile check is usually invisible but still renders a
          zero-height element; "contents" keeps it from adding a second gap
          above the email field (WHIT-479). */}
      <div id="turnstile-widget-container" className="contents"></div>
      <CustomInput
        name="email"
        value={formState.email}
        onChange={handleChange}
        onEnter={() => {}}
        placeholder={t('Email address') || ''}
        type="email"
        containerClassName="h-12"
      />
      {/* <CustomInput
        value={formState.firstName}
        onChange={handleChange}
        onEnter={() => {}}
        placeholder="Keresztnév"
        type="text"
        name="firstName"
      />
      <CustomInput
        value={formState.lastName}
        onChange={handleChange}
        onEnter={() => {}}
        placeholder="Vezetéknév"
        type="text"
        name="lastName"
      /> */}
      <CustomInput
        value={formState.password1}
        onChange={handleChange}
        onEnter={() => {}}
        placeholder={t('Password') || ''}
        type="password"
        name="password1"
        containerClassName="h-12"
      />
      <CustomInput
        value={formState.password2}
        onChange={handleChange}
        onEnter={() => {}}
        placeholder={t('Confirm password') || ''}
        type="password"
        name="password2"
        containerClassName="h-12"
      />
      <SolidButton buttonType="submit" className="w-full sm:w-3/5 self-center">
        {t('Sign up')}
      </SolidButton>
      <div className="relative flex pt-4 pb-2 items-center">
        <div className="flex-grow border-t border-gray-400"></div>
        <span className="flex-shrink mx-4 text-tt-light-gray-purple">
          {/* As on the login tab (WHIT-494). */}
          <button
            type="button"
            className="min-h-11 px-2 font-medium text-tt-dark-purple underline underline-offset-4 hover:no-underline"
            onClick={handleContinueWithoutRegistration}>
            {t('Continue without registration')}
          </button>
        </span>
        <div className="flex-grow border-t border-gray-400"></div>
      </div>
    </form>
  );
};

export default SignupForm;
